import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import {
    Plus, Clock, CheckCircle2, Circle, 
    User, Calendar, ClipboardList,
    X, Edit3, Trash2, Loader2,
    AlertCircle, MessageSquare
} from 'lucide-react';

const API_BASE = (import.meta.env.VITE_APP_API_URL && import.meta.env.VITE_APP_API_URL !== "undefined") ? import.meta.env.VITE_APP_API_URL : "https://apiessentialcarehomehealth.saasbizhub.com/api/v1";

export default function CrmTasks() {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [users, setUsers] = useState([]);
    const [leads, setLeads] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchData = async () => {
        const token = localStorage.getItem('admin_token');
        try {
            setLoading(true);
            const [tasksRes, usersRes, leadsRes] = await Promise.all([
                axios.get(`${API_BASE}/admin/crm/tasks`, { headers: { Authorization: `Bearer ${token}` } }),
                // Assuming there's a user listing endpoint, or just for assigning
                axios.get(`${API_BASE}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
                axios.get(`${API_BASE}/admin/crm/leads`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setTasks(tasksRes.data);
            setUsers(usersRes.data.data || usersRes.data);
            setLeads(leadsRes.data.data || leadsRes.data);
        } catch (err) {
            toast.error('Failed to load tasks');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const toggleComplete = async (task) => {
        const token = localStorage.getItem('admin_token');
        try {
            await axios.patch(`${API_BASE}/admin/crm/tasks/${task.id}/complete`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Task marked as complete');
            fetchData();
        } catch (err) {
            toast.error('Failed to update task');
        }
    };

    const handleCreateTask = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const data = Object.fromEntries(formData.entries());
        const token = localStorage.getItem('admin_token');

        try {
            setIsSubmitting(true);
            await axios.post(`${API_BASE}/admin/crm/tasks`, data, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Task created successfully');
            setIsCreateModalOpen(false);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to create task');
        } finally {
            setIsSubmitting(false);
        }
    };

    const isOverdue = (date) => !task.is_completed && new Date(date) < new Date();

    return (
        <div className="p-8 max-w-5xl mx-auto space-y-8">
            <Toaster position="top-right" />
            
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Tasks & Follow-ups</h1>
                    <p className="text-gray-500 mt-1">Stay on top of your lead interactions.</p>
                </div>
                <button 
                    onClick={() => setIsCreateModalOpen(true)}
                    className="inline-flex items-center px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-xl shadow-lg transition-all active:scale-95"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    New Task
                </button>
            </header>

            <div className="space-y-4">
                {loading ? (
                    Array(4).fill(0).map((_, i) => (
                        <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 animate-pulse h-24" />
                    ))
                ) : tasks.length > 0 ? (
                    tasks.map((task) => (
                        <motion.div 
                            layout
                            key={task.id}
                            className={`bg-white p-5 rounded-2xl border transition-all flex items-center gap-4 ${
                                task.is_completed 
                                ? 'border-gray-100 bg-gray-50/50 grayscale' 
                                : 'border-teal-100 shadow-sm hover:shadow-md'
                            }`}
                        >
                            <button 
                                onClick={() => !task.is_completed && toggleComplete(task)}
                                className={`p-2 rounded-full transition-colors ${
                                    task.is_completed 
                                    ? 'text-green-500' 
                                    : 'text-gray-300 hover:text-teal-600 hover:bg-teal-50'
                                }`}
                            >
                                {task.is_completed ? <CheckCircle2 className="w-8 h-8" /> : <Circle className="w-8 h-8" />}
                            </button>
                            
                            <div className="flex-1 min-w-0">
                                <h3 className={`font-bold text-lg truncate ${task.is_completed ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                                    {task.title}
                                </h3>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm">
                                    <span className="text-gray-500 flex items-center gap-1">
                                        <Calendar className={`w-4 h-4 ${!task.is_completed && new Date(task.due_date) < new Date() ? 'text-red-500' : ''}`} />
                                        {new Date(task.due_date).toLocaleDateString()}
                                    </span>
                                    {task.lead && (
                                        <span className="text-teal-600 font-medium flex items-center gap-1">
                                            <MessageSquare className="w-4 h-4" />
                                            Lead: {task.lead.name}
                                        </span>
                                    )}
                                    <span className="text-gray-500 flex items-center gap-1">
                                        <User className="w-4 h-4" />
                                        Assigned to: {task.assigned_to_user?.name || 'Self'}
                                    </span>
                                </div>
                            </div>
                        </motion.div>
                    ))
                ) : (
                    <div className="py-16 text-center text-gray-400 bg-white rounded-2xl border border-dashed border-gray-200">
                        <ClipboardList className="w-16 h-16 mx-auto mb-4 opacity-20" />
                        <p className="text-lg">No tasks for now. You're all caught up!</p>
                    </div>
                )}
            </div>

            {/* Create Modal */}
            <AnimatePresence>
                {isCreateModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsCreateModalOpen(false)} className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" />
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden relative" >
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                                <h2 className="text-xl font-bold text-gray-900">New Task</h2>
                                <button onClick={() => setIsCreateModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
                            </div>
                            <form onSubmit={handleCreateTask} className="p-6 space-y-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-700">Task Title *</label>
                                    <input name="title" required className="w-full px-4 py-2 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500" placeholder="e.g., Call back regarding insurance" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Due Date *</label>
                                        <input name="due_date" type="date" required className="w-full px-4 py-2 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Assign To *</label>
                                        <select name="assigned_to" required className="w-full px-4 py-2 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500">
                                            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-700">Related Lead</label>
                                    <select name="lead_id" className="w-full px-4 py-2 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500">
                                        <option value="">None</option>
                                        {leads.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                                    </select>
                                </div>
                                <div className="pt-4 flex justify-end gap-3">
                                    <button type="button" onClick={() => setIsCreateModalOpen(false)} className="px-6 py-2 text-gray-600 font-medium hover:bg-gray-100 rounded-xl">Cancel</button>
                                    <button type="submit" disabled={isSubmitting} className="px-6 py-2 bg-teal-600 text-white font-medium rounded-xl shadow-lg flex items-center gap-2">
                                        {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Task'}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
